import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiPlayCircle, FiFlag } from 'react-icons/fi';
import styles from './AdminInvestmentDetailPage.module.css';
import {
  AdminInvestmentsService,
  AdminInvestmentDetail,
  AdminInvestmentStatus
} from '../services/adminInvestments.service';

const nextStatus = (s: AdminInvestmentStatus): AdminInvestmentStatus => {
  if (s === 'Pendiente') return 'Aprobado';
  if (s === 'Aprobado') return 'Activa';
  if (s === 'Activa') return 'Finalizada';
  return s;
};

export default function AdminInvestmentDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const investmentId = Number(id);

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';

  const [data, setData] = useState<AdminInvestmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatMoney = (amount: number, currency: 'MXN' | 'USD') =>
    amount.toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AdminInvestmentsService.getById(investmentId);
      setData(res || null);
    } catch {
      setData(null);
      setError(t('admin.investment_detail_load_error') || 'No se pudo cargar el detalle.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(investmentId) || investmentId <= 0) {
      setLoading(false);
      setData(null);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentId]);

  const canApprove = data?.status === 'Pendiente';
  const canActivate = data?.status === 'Aprobado';
  const canComplete = data?.status === 'Activa';
  const canReject = data?.status === 'Pendiente' || data?.status === 'Aprobado';

  const primaryActionLabel = useMemo(() => {
    if (!data) return '';
    if (data.status === 'Pendiente') return t('admin.investment_action_approve') || 'Aprobar';
    if (data.status === 'Aprobado') return t('admin.investment_action_activate') || 'Marcar Activa';
    if (data.status === 'Activa') return t('admin.investment_action_complete') || 'Finalizar';
    return '';
  }, [data, t]);

  const onPrimaryAction = async () => {
    if (!data) return;
    const target = nextStatus(data.status);
    if (target === data.status) return;

    const ok = window.confirm(
      (t('admin.confirm_change_investment_status') || '¿Confirmar cambio de estatus?') + ` → ${target}`
    );
    if (!ok) return;

    setSaving(true);
    setError(null);

    const prev = data;
    setData({ ...data, status: target });

    try {
      await AdminInvestmentsService.updateStatus(data.id, { status: target });
      await load();
    } catch {
      setData(prev);
      setError(t('admin.investment_status_save_error') || 'No se pudo guardar el cambio de estatus.');
    } finally {
      setSaving(false);
    }
  };

  const onReject = async () => {
    if (!data) return;

    const reason =
      window.prompt(t('admin.reject_reason_prompt') || 'Motivo de rechazo (opcional):') || undefined;

    const ok = window.confirm(t('admin.confirm_reject_investment') || '¿Rechazar inversión?');
    if (!ok) return;

    setSaving(true);
    setError(null);

    const prev = data;
    setData({ ...data, status: 'Rechazado' });

    try {
      await AdminInvestmentsService.updateStatus(data.id, { status: 'Rechazado', reason });
      await load();
    } catch {
      setData(prev);
      setError(t('admin.investment_status_save_error') || 'No se pudo guardar el cambio de estatus.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading') || 'Cargando...'}</div>;

  if (!data) return <Navigate to="/admin/inversiones" replace />;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/admin/inversiones" className={styles.backLink}>
          <FiArrowLeft /> {t('common.back') || 'Volver'}
        </Link>

        <div className={styles.headerText}>
          <h1 className={styles.title}>
            {t('admin.investment_detail_title') || 'Detalle de inversión'} #{data.id}
          </h1>
          <p className={styles.subtitle}>
            {data.projectTitle} • {data.userName} ({data.userEmail})
          </p>
        </div>

        <div className={styles.headerActions}>
          {(canApprove || canActivate || canComplete) ? (
            <button type="button" className={styles.primaryBtn} onClick={onPrimaryAction} disabled={saving}>
              {data.status === 'Pendiente' ? <FiCheckCircle /> : data.status === 'Aprobado' ? <FiPlayCircle /> : <FiFlag />}
              {saving ? (t('common.saving') || 'Guardando...') : primaryActionLabel}
            </button>
          ) : null}

          {canReject ? (
            <button type="button" className={styles.dangerBtn} onClick={onReject} disabled={saving}>
              <FiXCircle /> {t('admin.reject') || 'Rechazar'}
            </button>
          ) : null}
        </div>
      </header>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.grid}>
        <article className={styles.card}>
          <div className={styles.cardTitle}>{t('admin.investment_card_summary') || 'Resumen'}</div>

          <div className={styles.kv}>
            <div className={styles.k}>
              {t('admin.table_amount') || 'Monto'}
            </div>
            <div className={styles.v}>
              {formatMoney(data.amount, data.currency)}
            </div>

            <div className={styles.k}>
              {t('admin.table_status') || 'Estatus'}
            </div>
            <div className={styles.v}>
              <span className={`${styles.statusChip} ${styles[`st_${data.status}`] || ''}`}>{data.status}</span>
            </div>

            <div className={styles.k}>Provider</div>
            <div className={styles.v}>{data.provider || '—'}</div>

            <div className={styles.k}>Transaction</div>
            <div className={styles.v}>{data.transactionCode || '—'}</div>

            <div className={styles.k}>Invoice</div>
            <div className={styles.v}>{data.invoiceId || '—'}</div>

            <div className={styles.k}>Order</div>
            <div className={styles.v}>{data.orderId || '—'}</div>

            <div className={styles.k}>{t('admin.investment_created_at') || 'Creado'}</div>
            <div className={styles.v}>{data.createdAt}</div>
          </div>
        </article>

        <article className={styles.card}>
          <div className={styles.cardTitle}>{t('admin.investment_card_entities') || 'Entidades'}</div>

          <div className={styles.kv}>
            <div className={styles.k}>{t('admin.table_project') || 'Proyecto'}</div>
            <div className={styles.v}>
              {data.projectTitle} <span className={styles.muted}>• ID {data.projectId}</span>
            </div>

            <div className={styles.k}>{t('admin.table_user') || 'Usuario'}</div>
            <div className={styles.v}>
              {data.userName} <span className={styles.muted}>• ID {data.userId}</span>
              <div className={styles.muted}>{data.userEmail}</div>
            </div>
          </div>
        </article>

        <article className={styles.card}>
          <div className={styles.cardTitle}>{t('admin.investment_timeline') || 'Historial'}</div>

          {Array.isArray(data.timeline) && data.timeline.length > 0 ? (
            <div className={styles.timeline}>
              {data.timeline.map((ev, idx) => (
                <div key={`${ev.at}-${idx}`} className={styles.timelineItem}>
                  <div className={styles.timelineAt}>{ev.at}</div>
                  <div className={styles.timelineBody}>
                    <div className={styles.timelineStatus}>{ev.status}</div>
                    {ev.by ? <div className={styles.timelineMeta}>{ev.by}</div> : null}
                    {ev.reason ? <div className={styles.timelineReason}>{ev.reason}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              {t('admin.investment_no_timeline') || 'Sin historial (backend no envió timeline).'}
            </div>
          )}
        </article>
      </section>
    </div>
  );
}
